import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, RefreshCw, Search, Users, Calendar, Plane, Lightbulb, Lock, LogOut } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "divemaster_admin_token";

const getToken = () => {
  try { return localStorage.getItem(TOKEN_KEY) || ""; } catch { return ""; }
};
const setToken = (t) => {
  try { if (t) localStorage.setItem(TOKEN_KEY, t); else localStorage.removeItem(TOKEN_KEY); } catch { /* noop */ }
};
const authHeaders = () => {
  const t = getToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
};

function fmtDate(iso) {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function fmtDay(iso) {
  if (!iso) return "-";
  try {
    // dates are stored as YYYY-MM-DD (input type=date)
    const [y, m, d] = iso.split("-");
    if (y && m && d) return `${d}/${m}/${y}`;
    return iso;
  } catch {
    return iso;
  }
}

function toCSV(rows) {
  const header = [
    "Nome", "E-mail", "CPF", "Telefone", "Profissão", "Nascimento", "Altura (cm)", "Peso (kg)", "Calçado",
    "Data Expedição (início)", "Data Expedição (fim)", "Nível", "Fará curso?", "Qual curso",
    "Roupa", "Colete", "Regulador", "Aluga lanterna (R$80/dia)", "Aluga computador (R$120/dia)",
    "Data voo ida", "Horário voo ida", "Data voo volta", "Horário voo volta",
    "Cidade voo", "Chegada em Caravelas",
    "Contato de emergência", "Restrição alimentar", "Alergia a medicação",
    "Observações", "Recebido em",
  ];
  const escape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const lines = [header.join(",")].concat(
    rows.map((r) =>
      [
        r.nome_completo, r.email, r.cpf, r.telefone, r.profissao, fmtDay(r.data_nascimento), r.altura_cm, r.peso_kg, r.numero_calcado,
        fmtDay(r.data_expedicao_inicio), fmtDay(r.data_expedicao_fim), r.nivel_atual, r.fara_curso ? "Sim" : "Não", r.qual_curso,
        r.equipamento_roupa ? "Sim" : "Não", r.equipamento_colete ? "Sim" : "Não", r.equipamento_regulador ? "Sim" : "Não", r.alugar_lanterna ? "Sim" : "Não", r.alugar_computador ? "Sim" : "Não",
        fmtDay(r.data_voo_ida), r.horario_voo_ida, fmtDay(r.data_voo_volta), r.horario_voo_volta,
        r.cidade_voo, r.chegada_caravelas,
        r.contato_emergencia, r.restricao_alimentar, r.alergia_medicacao,
        r.observacoes, fmtDate(r.created_at),
      ].map(escape).join(",")
    )
  );
  return "\uFEFF" + lines.join("\n");
}

// ==================== Login Screen ====================
function LoginScreen({ onSuccess }) {
  const [login, setLoginValue] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!login.trim() || !password.trim()) return;
    setError("");
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/auth/login`, { login: login.trim(), password });
      if (res.data?.token) {
        setToken(res.data.token);
        toast.success("Bem-vindo(a) ao painel");
        onSuccess?.();
      } else {
        setError("Resposta inválida do servidor.");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Login ou senha incorretos.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="flex min-h-screen items-center justify-center bg-slate-50 px-6" data-testid="admin-login-screen">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-navy text-white shadow-sm">
            <Lock className="h-6 w-6" />
          </div>
          <p className="text-[11px] font-black uppercase tracking-[0.24em] text-sun">
            Painel Interno
          </p>
          <h1 className="mt-2 font-display text-2xl font-black uppercase tracking-tight text-brand-navy">
            Expedição Abrolhos
          </h1>
          <p className="mt-2 text-xs text-slate-500">Acesso restrito à equipe.</p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
          data-testid="admin-login-form"
        >
          <label className="mb-2 block text-[10px] font-bold uppercase tracking-wider text-slate-500">
            Login
          </label>
          <input
            type="text"
            value={login}
            onChange={(e) => setLoginValue(e.target.value)}
            autoFocus
            autoComplete="username"
            spellCheck={false}
            className="mb-4 w-full rounded-xl border border-slate-200 px-3 py-3 text-sm text-brand-navy outline-none focus:border-brand-navy"
            data-testid="admin-login-input"
          />

          <label className="mb-2 block text-[10px] font-bold uppercase tracking-wider text-slate-500">
            Senha
          </label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            className="mb-4 w-full rounded-xl border border-slate-200 px-3 py-3 text-sm text-brand-navy outline-none focus:border-brand-navy"
            data-testid="admin-password-input"
          />

          {error && (
            <div
              className="mb-4 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-600"
              data-testid="admin-login-error"
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting || !login.trim() || !password.trim()}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-brand-navy py-3 text-xs font-bold uppercase tracking-widest text-white disabled:opacity-50"
            data-testid="admin-login-submit"
          >
            {submitting ? "Entrando..." : "Entrar no painel"}
          </button>
        </form>
      </div>
    </main>
  );
}

export default function DadosAbrolhosAdmin() {
  const [authed, setAuthed] = useState(!!getToken());
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState(null);

  const handleAuthError = (e) => {
    if (e?.response?.status === 401 || e?.response?.status === 403) {
      setToken("");
      setAuthed(false);
      toast.error("Sessão expirada. Faça login novamente.");
      return true;
    }
    return false;
  };

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${API}/abrolhos`, { headers: authHeaders() });
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      if (!handleAuthError(e)) setError("Não foi possível carregar os dados.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = "Abrolhos — Dados | Dive Master";
    if (authed) fetchData();
    else setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed]);

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, null, { headers: authHeaders() });
    } catch {
      // mesmo se falhar no servidor, encerra a sessão localmente
    }
    setToken("");
    setAuthed(false);
    toast.success("Sessão encerrada");
  };

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return items;
    return items.filter((r) =>
      [r.nome_completo, r.email, r.telefone, r.profissao, r.nivel_atual, r.qual_curso, r.cidade_voo, r.chegada_caravelas]
        .some((f) => String(f || "").toLowerCase().includes(term))
    );
  }, [items, q]);

  const stats = useMemo(() => {
    const lanterna = items.filter((i) => i.alugar_lanterna).length;
    const cursos = items.filter((i) => i.fara_curso).length;
    return { total: items.length, lanterna, cursos };
  }, [items]);

  const exportCSV = () => {
    const csv = toCSV(filtered);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const stamp = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `abrolhos-participantes-${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // ==== Not authenticated: show login screen ====
  if (!authed) {
    return <LoginScreen onSuccess={() => setAuthed(true)} />;
  }

  return (
    <main className="min-h-screen bg-slate-50" data-testid="admin-abrolhos-page">
      <div className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-6xl flex-col gap-3 px-6 py-6 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="font-display text-2xl font-black uppercase tracking-tight text-brand-navy sm:text-3xl">
              Expedição Abrolhos — Participantes
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              Cadastros recebidos pelo formulário /dadosabrolhos.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={fetchData}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              data-testid="abr-refresh"
            >
              <RefreshCw className="h-4 w-4" /> Atualizar
            </button>
            <button
              onClick={exportCSV}
              disabled={!filtered.length}
              className="inline-flex items-center gap-2 rounded-xl bg-brand-navy px-4 py-2 text-sm font-bold text-white shadow-sm hover:opacity-95 disabled:opacity-50"
              data-testid="abr-export"
            >
              <Download className="h-4 w-4" /> Exportar CSV
            </button>
            <button
              onClick={handleLogout}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-red-600 hover:bg-red-50"
              data-testid="abr-logout"
              title="Sair do painel"
            >
              <LogOut className="h-4 w-4" /> Sair
            </button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-6xl px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <StatCard label="Total de inscritos" value={stats.total} icon={Users} />
          <StatCard label="Vão fazer curso" value={stats.cursos} icon={Calendar} />
          <StatCard label="Alugam lanterna" value={stats.lanterna} icon={Lightbulb} />
        </div>

        {/* Search */}
        <div className="mt-6 flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 shadow-sm">
          <Search className="h-4 w-4 text-slate-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por nome, e-mail, telefone, profissão..."
            className="flex-1 bg-transparent px-1 py-1 text-sm outline-none placeholder:text-slate-400"
            data-testid="abr-search"
          />
          <span className="text-xs font-semibold text-slate-500">
            {filtered.length} de {items.length}
          </span>
        </div>

        {/* List */}
        <div className="mt-6 space-y-3">
          {loading ? (
            <div className="rounded-2xl bg-white p-10 text-center text-sm text-slate-500 shadow-sm">
              Carregando…
            </div>
          ) : error ? (
            <div className="rounded-2xl bg-white p-10 text-center text-sm text-red-600 shadow-sm">
              {error}
            </div>
          ) : filtered.length === 0 ? (
            <div className="rounded-2xl bg-white p-10 text-center text-sm text-slate-500 shadow-sm">
              Nenhum participante encontrado.
            </div>
          ) : (
            filtered.map((r) => {
              const open = expanded === r.id;
              return (
                <div
                  key={r.id}
                  className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm"
                  data-testid="abr-item"
                >
                  <button
                    onClick={() => setExpanded(open ? null : r.id)}
                    className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left hover:bg-slate-50"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-bold text-brand-navy">{r.nome_completo}</p>
                        <span className="rounded-full bg-ocean-mid/10 px-2 py-0.5 text-[10px] font-black uppercase tracking-wider text-ocean-mid">
                          {r.nivel_atual}
                        </span>
                        {r.alugar_lanterna && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-sun/15 px-2 py-0.5 text-[10px] font-black uppercase tracking-wider text-sun">
                            <Lightbulb className="h-3 w-3" /> Lanterna
                          </span>
                        )}
                        {r.fara_curso && (
                          <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-black uppercase tracking-wider text-emerald-700">
                            Curso
                          </span>
                        )}
                      </div>
                      <p className="mt-1 truncate text-sm text-slate-500">
                        {r.email} · Expedição: {fmtDay(r.data_expedicao_inicio)} a {fmtDay(r.data_expedicao_fim)}
                        {r.cidade_voo && (
                          <>
                            {" · "}
                            <Plane className="inline h-3 w-3" /> {r.cidade_voo}
                          </>
                        )}
                      </p>
                    </div>
                    <span className="text-xs font-semibold text-slate-400">{fmtDate(r.created_at)}</span>
                  </button>

                  {open && (
                    <div className="border-t border-slate-100 bg-slate-50/60 px-5 py-5 text-sm">
                      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                        <DetailField label="Nascimento" value={fmtDay(r.data_nascimento)} />
                        <DetailField label="Altura" value={`${r.altura_cm} cm`} />
                        <DetailField label="Peso" value={`${r.peso_kg} kg`} />
                        <DetailField label="Calçado" value={r.numero_calcado} />
                        <DetailField label="CPF" value={r.cpf || "-"} />
                        <DetailField label="Telefone" value={r.telefone || "-"} />
                        <DetailField label="Profissão" value={r.profissao || "-"} />
                        <DetailField label="Data expedição (início)" value={fmtDay(r.data_expedicao_inicio)} />
                        <DetailField label="Data expedição (fim)" value={fmtDay(r.data_expedicao_fim)} />
                        <DetailField label="Nível atual" value={r.nivel_atual} />
                      </div>
                      {r.fara_curso && (
                        <DetailField
                          label="Curso durante a expedição"
                          value={r.qual_curso || "(não especificado)"}
                          full
                        />
                      )}
                      <DetailField
                        label="Equipamentos alugados"
                        value={
                          [
                            r.equipamento_roupa && "Roupa",
                            r.equipamento_colete && "Colete",
                            r.equipamento_regulador && "Regulador",
                          ].filter(Boolean).join(", ") || "Nenhum"
                        }
                        full
                      />
                      <DetailField
                        label="Aluga lanterna (R$80/dia)"
                        value={r.alugar_lanterna ? "Sim" : "Não"}
                      />
                      <DetailField
                        label="Aluga computador (R$120/dia)"
                        value={r.alugar_computador ? "Sim" : "Não"}
                      />
                      <p className="mt-4 text-[10px] font-black uppercase tracking-wider text-slate-400">
                        Voo de ida
                      </p>
                      <div className="mt-1 grid grid-cols-2 gap-3 sm:grid-cols-3">
                        <DetailField label="Data do voo" value={fmtDay(r.data_voo_ida)} />
                        <DetailField label="Horário voo" value={r.horario_voo_ida || "-"} />
                      </div>
                      <p className="mt-4 text-[10px] font-black uppercase tracking-wider text-slate-400">
                        Voo de volta
                      </p>
                      <div className="mt-1 grid grid-cols-2 gap-3 sm:grid-cols-3">
                        <DetailField label="Data do voo" value={fmtDay(r.data_voo_volta)} />
                        <DetailField label="Horário voo" value={r.horario_voo_volta || "-"} />
                      </div>
                      <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
                        <DetailField label="Cidade voo" value={r.cidade_voo || "-"} />
                      </div>
                      <DetailField
                        label="Chegada em Caravelas"
                        value={r.chegada_caravelas || "-"}
                        full
                      />
                      <DetailField
                        label="Contato de emergência"
                        value={r.contato_emergencia || "-"}
                        full
                      />
                      <DetailField
                        label="Restrição alimentar"
                        value={r.restricao_alimentar || "Nenhuma"}
                        full
                      />
                      <DetailField
                        label="Alergia a medicação"
                        value={r.alergia_medicacao || "Nenhuma"}
                        full
                      />
                      {r.observacoes && (
                        <DetailField label="Observações" value={r.observacoes} full />
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </main>
  );
}

function StatCard({ label, value, icon: Icon }) {
  return (
    <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-navy/5 text-brand-navy">
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</p>
        <p className="font-display text-2xl font-black text-brand-navy">{value}</p>
      </div>
    </div>
  );
}

function DetailField({ label, value, full }) {
  return (
    <div className={`mt-3 ${full ? "col-span-2 sm:col-span-3" : ""}`}>
      <p className="text-[10px] font-black uppercase tracking-wider text-slate-500">{label}</p>
      <p className="mt-0.5 whitespace-pre-line text-sm font-semibold text-brand-navy">{value}</p>
    </div>
  );
}